import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Descriptions, Button, Tag, Row, Col, Statistic, Space, Divider } from 'antd';
import { EditOutlined, ArrowRightOutlined, ApartmentOutlined, TeamOutlined, IdcardOutlined, ReloadOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { departmentsApi } from '../../../utils/api';
import { DetailPageSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

const getDepartmentMembers = (department) => {
  return Array.isArray(department?.team_members) ? department.team_members : [];
};

const ViewDepartment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { canEdit } = useResourcePermissions('employees');
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/departments', { state: buildGoBackState() });
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDepartment();
  }, [id]);

  const fetchDepartment = async () => {
    setLoading(true);
    try {
      const response = await departmentsApi.getById(id);
      let departmentData = response.data;

      if (!Array.isArray(departmentData?.team_members)) {
        const listResponse = await departmentsApi.getAll({ id });
        const listDepartment = (listResponse.data || []).find((item) => String(item.id) === String(id));
        if (listDepartment) {
          departmentData = {
            ...departmentData,
            ...listDepartment,
            parent: departmentData?.parent || listDepartment.parent,
          };
        }
      }

      setDepartment(departmentData);
    } catch (error) {
      console.error('Error fetching department:', error);
      message.error('خطا در دریافت اطلاعات دپارتمان');
      navigate(returnToRef.current || '/hr/departments', { state: buildGoBackState() });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6">
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (!department) {
    return null;
  }

  const members = getDepartmentMembers(department);
  const membersCount = department.team_members_count ?? members.length;

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                <ApartmentOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">{department.team_name}</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">جزئیات دپارتمان و اطلاعات والد</p>
              </div>
            </div>
            <Space size="middle">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchDepartment}
                loading={loading}
                style={{ fontFamily: 'CustomFont' }}
              >
                بروزرسانی
              </Button>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={goBackToList}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
              {canEdit ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`/hr/departments/${id}/edit`, {
                      state: { returnTo: returnToRef.current, ...buildGoBackState() },
                    })
                  }
                  className="bg-blue-600 hover:bg-blue-700 border-0"
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش
                </Button>
              ) : null}
            </Space>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <Card
                  className="shadow-sm border border-gray-100 dark:border-gray-700 mb-4 bg-white dark:bg-gray-800"
                  title={
                    <div className="flex items-center gap-2">
                      <IdcardOutlined className="text-blue-500" />
                      <span style={{ fontFamily: 'CustomFont', fontSize: '18px', fontWeight: 600 }}>
                        اطلاعات اصلی
                      </span>
                    </div>
                  }
                >
                  <Descriptions
                    column={1}
                    bordered
                    size="middle"
                    style={{ fontFamily: 'CustomFont' }}
                    labelStyle={{
                      fontWeight: 600,
                      width: '200px',
                      backgroundColor: '#fafafa',
                    }}
                    contentStyle={{ backgroundColor: '#fff' }}
                  >
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <IdcardOutlined /> شناسه دپارتمان
                        </span>
                      }
                    >
                      <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 10px' }}>
                        #{department.id}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ApartmentOutlined /> نام دپارتمان
                        </span>
                      }
                    >
                      <span style={{ fontFamily: 'CustomFont', fontSize: '16px', fontWeight: 500 }}>
                        {department.team_name}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TeamOutlined /> دپارتمان والد
                        </span>
                      }
                    >
                      {department.parent ? (
                        <Tag
                          color="blue"
                          icon={<TeamOutlined />}
                          style={{
                            fontFamily: 'CustomFont',
                            fontSize: '14px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                          onClick={() => navigate(`/hr/departments/${department.parent.id}`)}
                          className="hover:shadow-md transition-all"
                        >
                          {department.parent.team_name}
                        </Tag>
                      ) : (
                        <Tag color="default" style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 10px' }}>
                          دپارتمان اصلی (بدون والد)
                        </Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <span style={{ fontFamily: 'CustomFont', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TeamOutlined /> تعداد کارمندان
                        </span>
                      }
                    >
                      <Tag color={membersCount > 0 ? 'blue' : 'default'} style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 10px' }}>
                        {membersCount} نفر
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card
                  className="shadow-sm border border-gray-100 dark:border-gray-700 mb-4 bg-white dark:bg-gray-800"
                  title={
                    <div className="flex items-center gap-2">
                      <TeamOutlined className="text-green-500" />
                      <span style={{ fontFamily: 'CustomFont', fontSize: '18px', fontWeight: 600 }}>
                        کارمندان دپارتمان
                      </span>
                    </div>
                  }
                >
                  {members.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {members.map((member) => (
                        <div
                          key={member.id || member.user_id}
                          className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40"
                        >
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-800 dark:text-gray-100 truncate" style={{ fontFamily: 'CustomFont' }}>
                              {member.user?.name || member.name || 'بدون نام'}
                            </div>
                            {member.employee_id ? (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" style={{ fontFamily: 'CustomFont' }}>
                                شناسه کارمند: {member.employee_id}
                              </div>
                            ) : null}
                          </div>
                          <Tag color="green" style={{ fontFamily: 'CustomFont', margin: 0 }}>
                            عضو
                          </Tag>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-6" style={{ fontFamily: 'CustomFont' }}>
                      کارمندی برای این دپارتمان ثبت نشده است.
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  className="shadow-sm border border-gray-100 dark:border-gray-700 mb-4 bg-white dark:bg-gray-800"
                  title={
                    <div className="flex items-center gap-2">
                      <TeamOutlined className="text-purple-500" />
                      <span style={{ fontFamily: 'CustomFont', fontSize: '18px', fontWeight: 600 }}>
                        خلاصه اطلاعات
                      </span>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                      <Statistic
                        title={<span style={{ fontFamily: 'CustomFont', color: '#666', fontSize: '14px' }}>شناسه</span>}
                        value={department.id}
                        valueStyle={{ color: '#1890ff', fontFamily: 'CustomFont', fontSize: '24px', fontWeight: 'bold' }}
                        prefix={<IdcardOutlined />}
                      />
                    </div>
                    <Divider style={{ margin: '16px 0' }} />
                    <div className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                      <div className="flex items-center justify-between">
                        <span style={{ fontFamily: 'CustomFont', color: '#666', fontSize: '14px' }}>
                          وضعیت
                        </span>
                        <Tag color="success" style={{ fontFamily: 'CustomFont' }}>
                          فعال
                        </Tag>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-2" style={{ fontFamily: 'CustomFont' }}>
                        این دپارتمان در حال استفاده است.
                      </p>
                    </div>
                    {department.parent && (
                      <>
                        <Divider style={{ margin: '16px 0' }} />
                        <div className="p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                          <div className="flex items-center justify-between">
                            <span style={{ fontFamily: 'CustomFont', color: '#666', fontSize: '14px' }}>
                              نوع
                            </span>
                            <Tag color="purple" style={{ fontFamily: 'CustomFont' }}>
                              زیرمجموعه
                            </Tag>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewDepartment;
